import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginGuestSchema } from '@tambola/shared';
import type { LoginGuestInput } from '@tambola/shared';
import { useAuthStore } from '../store/auth.store.js';

export function LoginScreen() {
  const navigate = useNavigate();
  const { user, guestLogin, googleLogin, facebookLogin, isLoading, error, isSessionChecked } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginGuestInput>({
    resolver: zodResolver(loginGuestSchema),
    defaultValues: {
      displayName: '',
    },
  });

  // Redirect to Home screen when authentication succeeds
  useEffect(() => {
    if (isSessionChecked && user) {
      navigate('/home');
    }
  }, [user, isSessionChecked, navigate]);

  const handleClearCache = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (const name of cacheNames) {
          await caches.delete(name);
        }
      }
      alert('Cache cleared! App is reloading...');
      window.location.href = window.location.origin + '?clear-cache=' + Date.now();
    } catch (err) {
      alert('Failed to clear cache. Please reload the page manually.');
    }
  };

  const onGuestSubmit = async (data: LoginGuestInput) => {
    await guestLogin(data.displayName);
  };

  const handleGoogleLogin = () => {
    const g = (window as any).google;
    if (g?.accounts?.oauth2) {
      const client = g.accounts.oauth2.initTokenClient({
        client_id: '875516538108-98r233qe9lmjglqm5ajssbabsfi2r5af.apps.googleusercontent.com',
        scope: 'email profile openid',
        callback: async (response: any) => {
          if (response.access_token) {
            await googleLogin(response.access_token);
          }
        },
      });
      client.requestAccessToken();
    } else {
      alert('Google Sign-In SDK is loading. Please try again in a moment.');
    }
  };

  const handleFacebookLogin = async () => {
    // Disabled
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 15, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <div className="relative flex h-screen w-screen items-center justify-center overflow-hidden bg-mesh bg-cover select-none">
      {/* Background Ambient Orbs */}
      <div className="absolute top-10 left-10 w-96 h-96 rounded-full bg-blue-500/10 blur-[100px]" />
      <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-purple-500/10 blur-[100px]" />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="flex flex-col items-center justify-center w-full max-w-[480px] px-6 z-20"
      >
        {/* Game Title Logo Header */}
        <motion.div variants={itemVariants} className="text-center mb-6">
          <h1
            style={{ textShadow: '0 4px 10px rgba(0,0,0,0.6), 0 0 20px rgba(124,58,237,0.3)' }}
            className="font-display text-4xl md:text-5xl font-extrabold tracking-wider bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-transparent drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
          >
            TAMBOLA
          </h1>
          <h2
            style={{ textShadow: '0 4px 10px rgba(0,0,0,0.6), 0 0 30px rgba(240,101,149,0.3)' }}
            className="font-display text-3xl md:text-4xl font-black tracking-widest text-gradient mb-1"
          >
            ONLINE
          </h2>
          <p className="text-xs md:text-sm text-amber-200/70 tracking-widest uppercase font-medium">
            Play Multiplayer Housie Anytime
          </p>
        </motion.div>

        {/* Login Card */}
        <motion.div
          variants={itemVariants}
          className="glass rounded-3xl p-6 md:p-8 w-full border border-white/10 shadow-glow-lg"
        >
          <form onSubmit={handleSubmit(onGuestSubmit)} className="space-y-4">
            {/* Display name field */}
            <div className="space-y-1">
              <label className="block text-xs font-bold uppercase tracking-wider text-surface-300">
                Display Name
              </label>
              <input
                type="text"
                {...register('displayName')}
                disabled={isLoading}
                placeholder="Enter nickname..."
                className="w-full px-4 py-3 rounded-xl bg-slate-950/50 border border-white/10 text-white placeholder-slate-500 font-medium tracking-wide focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/30 transition-all duration-300 shadow-inner"
              />
              {errors.displayName && (
                <span className="block text-[10px] font-semibold text-red-400 tracking-wide">
                  {errors.displayName.message}
                </span>
              )}
            </div>

            {/* Error notifications */}
            {error && (
              <div className="p-3 rounded-lg bg-red-950/40 border border-red-500/20 text-center">
                <span className="text-xs font-medium text-red-400">{error}</span>
              </div>
            )}

            {/* Main Primary Button: Guest Login */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isLoading}
              className="relative w-full py-3.5 rounded-xl bg-gradient-to-r from-purple-600 via-accent-500 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-black tracking-widest uppercase text-sm border border-purple-400/20 transition-all duration-300 shadow-glow-accent cursor-pointer flex items-center justify-center disabled:opacity-50"
            >
              {isLoading ? (
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : null}
              {isLoading ? 'Connecting...' : 'Play as Guest'}
            </motion.button>
          </form>

          {/* Divider line */}
          <div className="relative flex items-center justify-center my-6">
            <div className="w-full h-[1px] bg-white/10" />
            <span className="absolute px-4 bg-game-card text-[10px] font-bold text-surface-400 tracking-widest uppercase">
              OR
            </span>
          </div>

          {/* Social Sign-in layout */}
          <div className="grid grid-cols-2 gap-4">
            {/* Google button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="flex items-center justify-center space-x-2 py-3 rounded-xl bg-slate-900/60 hover:bg-slate-900 border border-white/5 text-surface-200 text-xs font-bold tracking-wider uppercase transition-colors duration-300 cursor-pointer disabled:opacity-50"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
                  fill="#4285F4"
                />
                <path
                  d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.853v2.332A9 9 0 0 0 9 18Z"
                  fill="#34A853"
                />
                <path
                  d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.59.1-1.164.282-1.706V4.962H.853a9 9 0 0 0 0 8.076l3.111-2.332Z"
                  fill="#FBBC05"
                />
                <path
                  d="M9 3.58c1.32 0 2.507.454 3.44 1.345l2.582-2.58C13.463.847 11.426 0 9 0A9 9 0 0 0 .853 4.962l3.111 2.332C4.672 5.164 6.656 3.58 9 3.58Z"
                  fill="#EA4335"
                />
              </svg>
              <span>Google</span>
            </motion.button>

            {/* Facebook button (Disabled, Grayscale) */}
            <motion.button
              disabled={true}
              className="flex items-center justify-center space-x-2 py-3 rounded-xl bg-slate-900/20 border border-white/2 text-surface-500 text-xs font-bold tracking-wider uppercase grayscale opacity-30 cursor-not-allowed pointer-events-none"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M18 9c0-4.97-4.03-9-9-9S0 4.03 0 9c0 4.49 3.29 8.22 7.59 8.9v-6.3H5.3v-2.6h2.29V6.9c0-2.27 1.35-3.52 3.41-3.52 1 0 2.04.18 2.04.18v2.24h-1.15c-1.13 0-1.48.7-1.48 1.42v1.71h2.52l-.4 2.6H10.42v6.3C14.71 17.22 18 13.49 18 9Z"
                  fill="#1877F2"
                />
              </svg>
              <span>Facebook</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Footer Support Links */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col items-center space-y-4 mt-6 opacity-60 text-xs tracking-wider"
        >
          <div className="flex items-center justify-center space-x-6">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                alert('Help manual coming soon.');
              }}
              className="hover:text-white transition-colors duration-300 font-medium"
            >
              Help
            </a>
            <span className="text-white/20">|</span>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                alert('Privacy Policy details coming soon.');
              }}
              className="hover:text-white transition-colors duration-300 font-medium"
            >
              Privacy
            </a>
            <span className="text-white/20">|</span>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                alert('Terms of Service details coming soon.');
              }}
              className="hover:text-white transition-colors duration-300 font-medium"
            >
              Terms
            </a>
          </div>

          <button
            onClick={handleClearCache}
            className="px-3 py-1 rounded-full border border-purple-500/30 hover:border-purple-500/70 bg-purple-950/20 text-[10px] font-bold text-purple-400 hover:text-white transition-all cursor-pointer flex items-center space-x-1"
          >
            <span>🔄</span>
            <span>Clear App Cache</span>
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
}
